import { ComponentType, Suspense } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import CircularProgress from '@mui/material/CircularProgress'
import { UserRole } from '@common/interfaces/user'
import { ROUTES } from '../../constants/routes'
import MainLayout from '../../pages/MainLayout'
import styles from './PrivateRoute.module.scss'
import { PropsFromRedux } from './index'

/**
 * Where a tasks-and-messaging user is allowed to be: Tasks, AIM, their
 * notifications, and their own account settings. Everything else in the portal is
 * denied server-side for that role, so rendering it would paint a page whose
 * every request 403s — and a 403 bounces the session to the dashboard (see the
 * permissionDenied saga), which is a worse place to land than a destination they
 * actually have.
 *
 * Prefix match, so `/messages/:channelId`, `/projects/:id` and the settings tabs
 * are covered.
 */
const TASKS_AND_MESSAGING_ROUTES = [
  ROUTES.MY_TASKS,
  ROUTES.TASK_PROJECTS,
  ROUTES.CUSTOM_FIELDS,
  ROUTES.MESSAGES,
  ROUTES.NOTIFICATIONS,
  ROUTES.SETTINGS_PROFILE,
  ROUTES.SETTINGS_SECURITY,
  ROUTES.CLIENT_SETTINGS_NOTIFICATIONS,
  ROUTES.SETTINGS_DISPLAY,
  ROUTES.SETTINGS_ACCESS_TOKENS,
]

const isTasksAndMessagingRoute = (pathname: string) =>
  TASKS_AND_MESSAGING_ROUTES.some((route) => pathname === route || pathname.startsWith(`${route}/`))

const pageLoader = <CircularProgress size="large" className={styles.pageLoader} />

export interface IProps extends PropsFromRedux {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- accepts any route component
  component: ComponentType<any>
  /**
   * Where to send an unauthenticated visitor instead of the login form, derived
   * from the path they asked for. The intake uses this: someone holding a
   * pre-login intake link has no account to log in with, so the login page would
   * be a dead end for them.
   */
  unauthenticatedRedirect?: (pathname: string) => string
  /** Restricts the page to these roles; anyone else is sent home. Omit for every signed-in role. */
  allowedRoles?: UserRole[]
}

const PrivateRoute = ({
  component: Component,
  isAuthenticated,
  isInitialized,
  role,
  unauthenticatedRedirect,
  allowedRoles,
}: IProps) => {
  const location = useLocation()
  if (!isInitialized) {
    return <MainLayout>{pageLoader}</MainLayout>
  }
  if (typeof window !== 'undefined') {
    window.localStorage.setItem('return_url', location.pathname + location.search)
  }
  if (!isAuthenticated) {
    return (
      <Navigate
        to={unauthenticatedRedirect ? unauthenticatedRedirect(location.pathname) : ROUTES.LOGIN}
        state={{
          from: location,
        }}
        replace
      />
    )
  }
  if (role === UserRole.TASKS_AND_MESSAGING && !isTasksAndMessagingRoute(location.pathname)) {
    return <Navigate to={ROUTES.MESSAGES} replace />
  }
  if (allowedRoles && !allowedRoles.includes(role)) {
    return <Navigate to={ROUTES.HOMEPAGE} replace />
  }

  // Route components are lazy-loaded (see routes.tsx). Suspending inside MainLayout keeps the
  // nav shell painted while the page's chunk arrives, rather than blanking the whole screen.
  return (
    <MainLayout>
      <Suspense fallback={pageLoader}>
        <Component />
      </Suspense>
    </MainLayout>
  )
}

export default PrivateRoute
